import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, RadioGroup, FormControlLabel, Radio, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Checkbox } from '@mui/material';
import AuthContext from '../../contexts/AuthContext';

/**
 * Step asking if the practice will perform in-house collections and, if yes,
 * surfaces the IFU agreement for review and acceptance.
 */
const InHouseCollectionStep = ({
  value,
  agreed,
  onChange,
  onAgreeChange,
  onBack,
  onContinue,
  continueLabel = 'Continue'
}) => {
  const { tokens } = useContext(AuthContext);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasViewedOnce, setHasViewedOnce] = useState(Boolean(agreed));

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        try { window.URL.revokeObjectURL(pdfUrl); } catch {}
      }
    };
  }, [pdfUrl]);

  const openAgreement = async () => {
    setError('');
    setLoading(true);
    setDialogOpen(true);
    setHasViewedOnce(true);
    try {
      const res = await fetch(`${apiBase}/agreements/key/ifu/document`, {
        headers: { 'Authorization': `Bearer ${tokens.id_token}` },
      });
      if (!res.ok) throw new Error('Failed to load agreement');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (e) {
      setError(e.message || 'Failed to load agreement');
    } finally {
      setLoading(false);
    }
  };

  const closeAgreement = () => {
    setDialogOpen(false);
  };

  const canContinue = value === false || (value === true && agreed === true);

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        Collections on-site
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Do you plan to perform specimen collections at your practice location?
      </Typography>
      <Box sx={{ mt: 2 }}>
        <RadioGroup
          value={value === true ? 'yes' : value === false ? 'no' : ''}
          onChange={(e) => onChange(e.target.value === 'yes')}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </Box>
      {value === true && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            To perform collections on-site, please review and accept the Instructions for Use (IFU).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
            <Button variant="outlined" onClick={openAgreement}>View IFU</Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed || false}
                  onChange={(e) => onAgreeChange(e.target.checked)}
                  disabled={!hasViewedOnce}
                />
              }
              disabled={!hasViewedOnce}
              label="I have reviewed the IFU and agree"
            />
            {!hasViewedOnce && (
              <Typography variant="caption" color="text.secondary">
                Click "View IFU" to enable agreement.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>Back</Button>
        <Button variant="contained" onClick={onContinue} disabled={!canContinue}>
          {continueLabel}
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={closeAgreement} maxWidth="md" fullWidth>
        <DialogTitle>Instructions for Use (IFU)</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 400 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && pdfUrl && (
            <iframe title="ifu" src={pdfUrl} style={{ width: '100%', height: '70vh', border: 'none' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAgreement}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InHouseCollectionStep;
